/**
 * Share JWT across apex + subdomains for each production host (see `sharedAuthCookieParentDomain`).
 *
 * localStorage is per-origin; a cookie with Domain=.<parent> is visible on matching subdomains.
 */

import { sharedAuthCookieParentDomain } from './hostPairing';

const COOKIE_NAME = 'vaiu_auth_token';
const LOCAL_KEY = 'authToken';
/** Long-lived cookie; JWT expiry is still enforced by the API */
const MAX_AGE_SEC = 60 * 60 * 24 * 400;

/**
 * Trim storage noise: users sometimes paste full `Authorization: Bearer …` lines or duplicate `Bearer `.
 * API gateways (especially AWS) mis-parse malformed Authorization values.
 */
export function normalizeBearerAuthToken(raw: string | null | undefined): string | null {
  if (raw == null || typeof raw !== 'string') return null;
  let t = raw.trim();
  if (!t) return null;
  // Strip accidental "Bearer" prefix(es) (case-insensitive), including pasted header lines.
  const headerPrefix = /^authorization\s*:\s*/i;
  if (headerPrefix.test(t)) {
    t = t.replace(headerPrefix, '').trim();
  }
  for (let i = 0; i < 5; i++) {
    const m = t.match(/^Bearer\s+/i);
    if (!m) break;
    t = t.slice(m[0].length).trim();
  }
  return t.length > 0 ? t : null;
}

/**
 * Returns the parent domain for shared auth cookies, or null if SSO cookies are not used.
 */
export function getVaiuCrossSubdomainCookieDomain(): string | null {
  if (typeof window === 'undefined') return null;
  return sharedAuthCookieParentDomain(window.location.hostname);
}

export function readCrossSubdomainAuthCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const prefix = `${COOKIE_NAME}=`;
  for (const part of document.cookie.split(';')) {
    const c = part.trim();
    if (c.startsWith(prefix)) {
      const raw = c.slice(prefix.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return null;
}

export function setCrossSubdomainAuthCookie(token: string): void {
  if (typeof document === 'undefined') return;
  const domain = getVaiuCrossSubdomainCookieDomain();
  if (!domain) return;
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(token)}; path=/; domain=${domain}; SameSite=Lax; Max-Age=${MAX_AGE_SEC}${secure}`;
}

export function clearCrossSubdomainAuthCookie(): void {
  if (typeof document === 'undefined') return;
  const domain = getVaiuCrossSubdomainCookieDomain();
  if (!domain) return;
  const secure =
    typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  // Match setCrossSubdomainAuthCookie attributes so browsers reliably expire the cookie
  document.cookie = `${COOKIE_NAME}=; path=/; domain=${domain}; SameSite=Lax; Max-Age=0${secure}`;
}

/**
 * Prefer localStorage; if empty, hydrate from shared subdomain cookie (logged in on sibling host).
 */
export function resolveSharedAuthToken(): string | null {
  if (typeof localStorage === 'undefined') return null;
  const fromLsRaw = localStorage.getItem(LOCAL_KEY);
  const fromLs = normalizeBearerAuthToken(fromLsRaw);
  if (fromLs) {
    if (fromLsRaw && fromLs !== fromLsRaw) {
      localStorage.setItem(LOCAL_KEY, fromLs);
    }
    return fromLs;
  }
  const fromCookieRaw = readCrossSubdomainAuthCookie();
  const fromCookie = normalizeBearerAuthToken(fromCookieRaw);
  if (fromCookie) {
    localStorage.setItem(LOCAL_KEY, fromCookie);
    return fromCookie;
  }
  return null;
}
